import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePermissionAssignment,
  fetchAllPermissionsForCategory,
  fetchAllUserGroups,
  fetchAllUsers,
  fetchPermissionAssignmentsMatrix,
  selectAllGroups,
  selectAllUsers,
  selectPermissionAssignments,
  selectPermissions,
  updateUserGroupPermissionAssignment,
  updateUserPermissionAssignment,
} from "./PermissionAssignmentsSlice";
import { ThreeStateCheckbox } from "cpl-react-common/components/Inputs/ThreeStateCheckbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPersonCirclePlus, faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { selectSelectedPermissionCategory } from "./PermissionCategoriesSlice";
import NewPermissionModal from "./NewPermissionModal";
import NewPermissionGroupModal from "./NewPermissionGroupModal";

const PermissionAssignmentsList: React.FC = () => {
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const permissionAssignments = useSelector(selectPermissionAssignments);
  const allUsers = useSelector(selectAllUsers);
  const allGroups = useSelector(selectAllGroups);
  const allPermissions = useSelector(selectPermissions);
  const permissionCategory = useSelector(selectSelectedPermissionCategory);
  const [showNewPermissionModal, setShowNewPermissionModal] = useState(false);
  const [showNewPermissionGroupModal, setShowNewPermissionGroupModal] = useState(false);

  function userCheckboxClicked(
    permissionID: number,
    userID: string,
    permissionAssignmentID: number,
    checked?: boolean
  ) {
    if (checked == null) {
      thunkDispatch(updateUserPermissionAssignment({ userID: userID, permissionID: permissionID, grant: false }));
    } else if (!checked) {
      thunkDispatch(updateUserPermissionAssignment({ userID: userID, permissionID: permissionID, grant: true }));
    } else if (checked) {
      thunkDispatch(deletePermissionAssignment(permissionAssignmentID));
    }
  }

  function userGroupCheckboxClicked(
    permissionID: number,
    groupID: number,
    permissionAssignmentID: number,
    checked?: boolean
  ) {
    if (checked == null) {
      thunkDispatch(
        updateUserGroupPermissionAssignment({ userGroupID: groupID, permissionID: permissionID, grant: false })
      );
    } else if (!checked) {
      thunkDispatch(
        updateUserGroupPermissionAssignment({ userGroupID: groupID, permissionID: permissionID, grant: true })
      );
    } else if (checked) {
      thunkDispatch(deletePermissionAssignment(permissionAssignmentID));
    }
  }

  const filteredUserList = useMemo(() => {
    const filtered = allUsers.filter((element) => {
      return (permissionAssignments.rows?.map((i) => i.userID).indexOf(element.userID) ?? 0) < 0;
    });
    return filtered.map((i) => ({
      value: i.userID!,
      label: i.emailAddress!,
    }));
  }, [allUsers, permissionAssignments]);

  const filteredGroupList = useMemo(() => {
    const filtered = allGroups.filter((element) => {
      return (permissionAssignments.rows?.map((i) => i.userGroupID).indexOf(element.userGroupID) ?? 0) < 0;
    });
    return filtered.map((i) => ({
      value: i.userGroupID!,
      label: i.groupName!,
    }));
  }, [allGroups, permissionAssignments]);

  const permissionsList = useMemo(() => {
    return allPermissions.map((i) => ({
      value: i.permissionID!,
      label: i.name!,
    }));
  }, [allPermissions]);

  useEffect(() => {
    thunkDispatch(fetchAllPermissionsForCategory(permissionCategory.permissionCategoryID!));
    thunkDispatch(fetchPermissionAssignmentsMatrix(permissionCategory.permissionCategoryID!));
    thunkDispatch(fetchAllUsers());
    thunkDispatch(fetchAllUserGroups());
  }, [dispatch, thunkDispatch, permissionCategory]);

  return (
    <Permission permissionPredicate={(permission) => permission === Permissions.ViewPermissions}>
      <Permission permissionPredicate={(permission) => permission === Permissions.ManagePermissions}>
        <NewPermissionModal
          showNewPermissionModal={showNewPermissionModal}
          setShowNewPermissionModal={setShowNewPermissionModal}
          filteredUserList={filteredUserList}
          permissionsList={permissionsList}
        />
        <NewPermissionGroupModal
          showNewPermissionGroupModal={showNewPermissionGroupModal}
          setShowNewPermissionGroupModal={setShowNewPermissionGroupModal}
          filteredGroupList={filteredGroupList}
          permissionsList={permissionsList}
        />
      </Permission>
      <div className="card">
        <div className="card-header">
          <div className="card-title">{permissionCategory.name} Permissions</div>
          <div className="card-toolbar">
            <Permission permissionPredicate={(permission) => permission === Permissions.ManagePermissions}>
              <button
                data-test-id="permissions-matrix-table__new-permission-button"
                className="btn btn-primary btn-icon border-0 me-1"
                onClick={() => setShowNewPermissionModal(true)}
              >
                <FontAwesomeIcon icon={faUserPlus as IconProp} />
              </button>
              <button
                data-test-id="permissions-matrix-table__new-permission-group-button"
                className="btn btn-primary btn-icon border-0 me-1"
                onClick={() => setShowNewPermissionGroupModal(true)}
              >
                <FontAwesomeIcon icon={faPersonCirclePlus as IconProp} />
              </button>
            </Permission>
          </div>
        </div>
        <div className="card-body">
          <b>Key:</b>
          <div className="d-flex gap-4 mb-4">
            <div className="d-flex gap-2">
              <ThreeStateCheckbox
                data-test-id="permissions-matrix-table__granted-key-indicator"
                currentStatus={true}
                interactive={false}
              />
              <span>Permission Granted</span>
            </div>
            <div className="d-flex gap-2">
              <ThreeStateCheckbox
                data-test-id="permissions-matrix-table__unset-key-indicator"
                currentStatus={undefined}
                interactive={false}
              />
              <span>Permission Unset (Inherits)</span>
            </div>
            <div className="d-flex gap-2">
              <ThreeStateCheckbox
                data-test-id="permissions-matrix-table__denied-key-indicator"
                currentStatus={false}
                interactive={false}
              />
              <span>Permission Denied</span>
            </div>
          </div>
          {(permissionAssignments.rows !== undefined && permissionAssignments.rows.length > 0 && (
            <div className="table-responsive">
              <table className="table table-row-dashed">
                <thead>
                  <tr>
                    <th key="assigneeName" className="text-dark fw-bold mb-1 fs-6">
                      Assignee Name
                    </th>
                    {allPermissions.map((permission) => (
                      <th key={permission.permissionID?.toString()} className="text-dark fw-bold mb-1 fs-6">
                        {permission.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {permissionAssignments.rows.map(
                    (row) =>
                      (row.userGroupID === null && (
                        <tr key={row.rowID}>
                          <td>{row.name}</td>
                          {row.assignments?.map((cell) => (
                            <td key={cell.permissionID}>
                              <div
                                data-test-id={`permissions-matrix-table__user-permission-checkbox-${row.userID}-${cell.permissionID}`}
                                onClick={() =>
                                  userCheckboxClicked(
                                    cell.permissionID!,
                                    row.userID!,
                                    cell.permissionAssignmentID!,
                                    cell.permissionGranted
                                  )
                                }
                              >
                                <ThreeStateCheckbox
                                  data-test-id={`permissions-matrix-table__user-permission-status-indicator-${row.userID}-${cell.permissionID}`}
                                  currentStatus={cell.permissionGranted}
                                />
                              </div>
                            </td>
                          ))}
                        </tr>
                      )) ||
                      (row.userID === null && (
                        <tr key={row.rowID}>
                          <td>{row.name}</td>
                          {row.assignments?.map((cell) => (
                            <td
                              key={cell.permissionID}
                              data-test-id={`permissions-matrix-table__user-group-permission-checkbox-${row.userGroupID}-${cell.permissionID}`}
                              onClick={() =>
                                userGroupCheckboxClicked(
                                  cell.permissionID!,
                                  row.userGroupID!,
                                  cell.permissionAssignmentID!,
                                  cell.permissionGranted
                                )
                              }
                            >
                              <ThreeStateCheckbox
                                data-test-id={`permissions-matrix-table__user-group-permission-status-indicator-${row.userGroupID}-${cell.permissionID}`}
                                currentStatus={cell.permissionGranted}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          )) || <div> No permissions set for this category. Please add a user or a group to manage permissions</div>}
        </div>
      </div>
    </Permission>
  );
};

export default PermissionAssignmentsList;
